import React, { useState } from 'react';
import { withLocalePrefix } from 'shared/utils/translation';
import styled from 'styled-components';
import { colors } from '@grnhse/seedling/lib/azalea/constants';
import { PageHeader, BodyText } from '@grnhse/seedling/lib/azalea/typography';
import { Link } from '@grnhse/seedling/lib/birch/typography';
import ReminderIcon from '@grnhse/seedling/lib/birch/icons/reminder';
import LocationIcon from '@grnhse/seedling/lib/birch/icons/location';
import VideoCallIcon from '@grnhse/seedling/lib/birch/icons/video_call';
import ChatIcon from '@grnhse/seedling/lib/birch/icons/chat';
import { Body, MainHeader } from '@grnhse/bonsai/lib/components/typography';
import { TertiaryButton } from '@grnhse/bonsai/lib/components/buttons';
import Logo from '../../components/logo';

type Props = {
  jobTitle: string;
  interviewName: string;
  interviewLength: number;
  interviewLocation: string;
  interviewInvitation: string;
  logoUrl: string | null;
  useVideo: boolean;
  videoConferencingProvider: string | null | undefined;
  bonsaiEnabled: boolean;
};

const t = withLocalePrefix('self_schedule');

const LogoWrapper = styled.div`
  display: flex;
  margin-left: 25px;
  margin-top: 35px;
`;

export default function InterviewDetails(props: Props) {
  const [showDetails, toggleShowDetails] = useState(false);

  const {
    jobTitle,
    interviewName,
    interviewInvitation,
    interviewLength,
    interviewLocation,
    logoUrl,
    useVideo,
    videoConferencingProvider,
    bonsaiEnabled,
  } = props;

  const DetailBody = bonsaiEnabled ? Body : 'span';

  const detailsToggleLabel = showDetails
    ? t('candidate_view.hide_details')
    : t('candidate_view.mobile.show_details');

  return (
    <Wrapper className={showDetails ? 'full-screen' : ''}>
      {bonsaiEnabled && logoUrl && (
        <LogoWrapper>
          <Logo logoUrl={logoUrl} />
        </LogoWrapper>
      )}
      <Content>
        <Header bonsaiEnabled={bonsaiEnabled}>
          {t('candidate_view.mobile.title')}
        </Header>
        <Subheader>
          <StyledBodyText
            bonsaiEnabled={bonsaiEnabled}
            showDetails={showDetails}
          >
            {jobTitle}
          </StyledBodyText>
          <StyledBodyText
            bonsaiEnabled={bonsaiEnabled}
            showDetails={showDetails}
          >
            {t('candidate_view.mobile.bullet')}
          </StyledBodyText>
          <StyledBodyText
            bonsaiEnabled={bonsaiEnabled}
            showDetails={showDetails}
          >
            {interviewName}
          </StyledBodyText>
        </Subheader>

        {showDetails && (
          <>
            <DetailWrapper>
              <ReminderIcon color={colors.darkerGrey} />
              <DetailBody>
                {interviewLength} {t('minutes')}
              </DetailBody>
            </DetailWrapper>

            {interviewLocation && (
              <DetailWrapper>
                <LocationIcon />
                <DetailBody>{interviewLocation}</DetailBody>
              </DetailWrapper>
            )}

            {useVideo && (
              <DetailWrapper>
                <VideoCallIcon />
                <DetailBody>
                  {t('candidate_view.video_link', {
                    provider: videoConferencingProvider,
                  })}
                </DetailBody>
              </DetailWrapper>
            )}

            {interviewInvitation && (
              <DetailWrapper>
                <ChatIcon />
                <DetailBody>
                  <EmailText
                    /* eslint-disable-next-line react/no-danger */
                    dangerouslySetInnerHTML={{
                      __html: interviewInvitation,
                    }}
                  />
                </DetailBody>
              </DetailWrapper>
            )}
          </>
        )}
        <LinkWrapper className={showDetails ? 'full-screen' : ''}>
          {bonsaiEnabled ? (
            <TertiaryButton
              ariaLabel={detailsToggleLabel}
              onClick={() => toggleShowDetails(!showDetails)}
            >
              {detailsToggleLabel}
            </TertiaryButton>
          ) : (
            <Link onClick={() => toggleShowDetails(!showDetails)}>
              {detailsToggleLabel}
            </Link>
          )}
        </LinkWrapper>
      </Content>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  flex: 1;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.05);
  top: 0;
  position: fixed;
  width: 100vw;
  background: white;
  z-index: 100;

  &.full-screen {
    position: absolute;
    top: 0;
    z-index: 100;
    background-color: white;
    height: 100vh;
  }
`;

const Content = styled.div`
  padding: 0 25px;
`;

const StyledPageHeader = styled(PageHeader)`
  margin-bottom: 8px;
`;

const Header = ({
  bonsaiEnabled,
  children,
}: {
  bonsaiEnabled: boolean;
  children: React.ReactNode;
}) => {
  if (bonsaiEnabled) {
    return (
      <div style={{ marginBottom: '8px' }}>
        <MainHeader>{children}</MainHeader>
      </div>
    );
  }

  return <StyledPageHeader>{children}</StyledPageHeader>;
};

const Subheader = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const StyledBodyText = ({
  bonsaiEnabled,
  children,
  showDetails,
}: {
  bonsaiEnabled: boolean;
  children: React.ReactNode;
  showDetails?: boolean;
}) => {
  if (bonsaiEnabled) {
    return (
      <BodyWrapper showDetails={showDetails}>
        <Body>{children}</Body>
      </BodyWrapper>
    );
  }

  return (
    <BodyWrapper showDetails={showDetails}>
      <BodyText>{children}</BodyText>
    </BodyWrapper>
  );
};

const BodyWrapper = styled.div<{ showDetails?: boolean }>`
  margin: 0 0 8px;
  text-overflow: ${({ showDetails }) => !showDetails && 'ellipsis'};
  overflow: ${({ showDetails }) => !showDetails && 'hidden'};
  white-space: ${({ showDetails }) => !showDetails && 'nowrap'}; ;
`;

const DetailWrapper = styled.div`
  margin-bottom: 20px;
  display: flex;
  align-items: flex-start;

  & > svg {
    flex: 0 0 20px;
    margin-right: 15px;
  }

  & > span {
    flex: 1 1 auto;
    max-height: 350px;

    p {
      margin-top: 0;
    }

    img {
      max-width: 250px;
      height: auto;
    }
  }

  & > p {
    word-break: break-word;
    overflow-y: auto;
    overflow-x: hidden;

    & > span {
      flex: 1 1 auto;
      max-height: 350px;

      p {
        margin-top: 0;
      }

      img {
        max-width: 250px;
        height: auto;
      }
    }
  }
`;

const EmailText = styled.span`
  overflow-y: auto;
`;

const LinkWrapper = styled.div`
  margin-bottom: 20px;

  &.full-screen {
    position: fixed;
    bottom: 50px;
  }
`;
